import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { toast } from 'sonner'
import { 
  ArrowLeft, Users, Smartphone, Globe, MessageSquare,
  Monitor, Apple, CircleDot, Target
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`
const PILOT_GOAL = 50

export default function PilotMetricsPage() {
  const navigate = useNavigate()
  const { token, user } = useAuth()
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await axios.get(`${API}/pilot/metrics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setMetrics(response.data)
      } catch (error) {
        console.error('Error fetching metrics:', error)
        if (error.response?.status === 403) {
          toast.error('Acceso denegado')
          navigate('/')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchMetrics()
  }, [token, navigate])

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'ios': return Apple
      case 'android': return Smartphone
      case 'desktop': return Monitor
      default: return CircleDot
    }
  }

  const getDeviceLabel = (device) => {
    switch (device) {
      case 'ios': return 'iOS'
      case 'android': return 'Android'
      case 'desktop': return 'Desktop'
      default: return device
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center gap-3 p-4 max-w-4xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">Métricas del Piloto</h1>
            <p className="text-xs text-muted-foreground">Panel de administración</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 pb-24">
        {metrics && (
          <>
            {/* Pilot Progress Card - Hero */}
            <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Testers del piloto</p>
                    <p className="text-3xl font-bold">
                      {metrics.total_users} <span className="text-lg font-normal text-muted-foreground">/ {PILOT_GOAL}</span>
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Target className="w-7 h-7 text-primary" />
                  </div>
                </div>
                <div className="w-full h-3 bg-primary/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((metrics.total_users / PILOT_GOAL) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-right">
                  {Math.round((metrics.total_users / PILOT_GOAL) * 100)}% de la meta
                </p>
              </CardContent>
            </Card>

            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold">{metrics.total_users}</p>
                      <p className="text-xs text-muted-foreground">Usuarios totales</p>
                    </div>
                    <Users className="w-8 h-8 text-primary/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold">{metrics.installed_pwa}</p>
                      <p className="text-xs text-muted-foreground">Instalados (PWA)</p>
                    </div>
                    <Smartphone className="w-8 h-8 text-emerald-500/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold">{metrics.browser_users}</p>
                      <p className="text-xs text-muted-foreground">Navegador web</p>
                    </div>
                    <Globe className="w-8 h-8 text-blue-500/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold">{metrics.feedback_sent}</p>
                      <p className="text-xs text-muted-foreground">Feedback enviado</p>
                    </div>
                    <MessageSquare className="w-8 h-8 text-amber-500/50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Device Breakdown */}
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Por dispositivo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(metrics.by_device || {}).map(([device, count]) => {
                    const Icon = getDeviceIcon(device)
                    const percentage = metrics.total_users > 0 
                      ? Math.round((count / metrics.total_users) * 100) 
                      : 0
                    return (
                      <div key={device} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span>{getDeviceLabel(device)}</span>
                            <span className="font-medium">{count} ({percentage}%)</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary/60 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {Object.keys(metrics.by_device || {}).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay datos de dispositivos todavía
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Resumen del piloto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Usuarios en piloto</span>
                    <span className="font-medium">{metrics.total_users}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Instalados como app</span>
                    <span className="font-medium">{metrics.installed_pwa}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Usuarios web</span>
                    <span className="font-medium">{metrics.browser_users}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">Feedback recibido</span>
                    <span className="font-medium">{metrics.feedback_sent}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Conversion rates */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <h3 className="text-sm font-medium mb-3">Tasas de conversión</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">PWA Adoption</p>
                  <p className="text-lg font-bold">
                    {metrics.total_users > 0 
                      ? Math.round((metrics.installed_pwa / metrics.total_users) * 100) 
                      : 0}%
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Feedback Rate</p>
                  <p className="text-lg font-bold">
                    {metrics.total_users > 0 
                      ? Math.round((metrics.feedback_sent / metrics.total_users) * 100) 
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
